import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";

const HelpSupport = () => {
  // Custom styling for TableCells without background color and border
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    "&:hover": {
      backgroundColor: "#e8e8e8", // Light hover effect
    },
    textAlign: "center",
    border: "1px solid #e6e1e1",
  }));

  // Custom styling for TableRows without background color
  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(even)": {
      backgroundColor: "transparent", // Remove alternating row background color
    },
  }));
  
  const dataLevel1 = [
    { sl: 1, text: "Introduction to Vedānta", noOfClasses: 16 },
    { sl: 2, text: "Tattvabodha", noOfClasses: 22 },
    { sl: 3, text: "Bhagavad-gītā Summary", noOfClasses: 22 },
    { sl: 4, text: "Bhajagovindam", noOfClasses: 12 },
    { sl: 5, text: "Bhagavad-gītā, Chapters 1 To 6", noOfClasses: 99 },
    { sl: 6, text: "Sādhana-pañcakam and Values", noOfClasses: 13 },
    { sl: 7, text: "Bhagavad-gītā, Chapters 7 To 12", noOfClasses: 65 },
    { sl: 8, text: "Jīvayātrā and Manīṣā-pañcakam", noOfClasses: 20 },
    { sl: 9, text: "Bhagavad-gītā, Chapters 13 to 18", noOfClasses: 79 },
    { sl: 10, text: "Vivekacūḍāmaṇi- selected 108 verses", noOfClasses: 46 },
    { sl: "", text: "", noOfClasses: "_____" },
    { sl: "", text: "Total", noOfClasses: 396 },
    { sl: "", text: "", noOfClasses: "_____" },
  ];

  const dataLevel2 = [
    { sl: 1, text: "Essence of Upaniṣads", noOfClasses: 30 },
    { sl: 2, text: "Upadeśasāra", noOfClasses: 11 },
    { sl: 3, text: "Muṇḍaka-upaniṣad", noOfClasses: 38 },
    { sl: 4, text: "Sarva-vedānta-siddhānta-sārasaṇgraha", noOfClasses: 29 },
    { sl: 5, text: "Kena-upaniṣad", noOfClasses: 12 },
    { sl: 6, text: "Ātmabodha", noOfClasses: 30 },
    { sl: 7, text: "Kaṭha-upaniṣad", noOfClasses: 45 },
    { sl: 8, text: "Aparokṣānubhūti", noOfClasses: 42 },
    { sl: 9, text: "Kaivalya-upaniṣad", noOfClasses: 13 },
    { sl: 10, text: "Vākyavṛtti", noOfClasses: 19 },
    { sl: 11, text: "Taittirīya-upaniṣad", noOfClasses: 51 },
    { sl: 12, text: "Dṛg-dṛśyā-viveka", noOfClasses: 21 },
    { sl: 13, text: "Māṇḍūkya-upaniṣad & Kārikā", noOfClasses: 81 },
    { sl: "", text: "", noOfClasses: "_____" },
    { sl: "", text: "Total", noOfClasses: 422 },
    { sl: "", text: "", noOfClasses: "_____" },
  ];

  const dataLevel3 = [
    { sl: 1, text: "Sad-darśanam", noOfClasses: 24 },
    {
      sl: 2,
      text: "Dakṣināmūrti-stotram and portions of Śivānandalaharī",
      noOfClasses: 21,
    },
    { sl: 3, text: "Advaita-makaranda", noOfClasses: 14 },
    { sl: 4, text: "Brahmasūtras (Sūtras 1 to 4)", noOfClasses: 35 },
    { sl: 5, text: "Aṣṭāvakra-gītā", noOfClasses: 74 },
    { sl: 6, text: "Vivekacūḍāmaṇi- meditation verses", noOfClasses: 20 },
    { sl: "", text: "", noOfClasses: "_____" },
    { sl: "", text: "Total", noOfClasses: 188 },
    { sl: "", text: "", noOfClasses: "_____" },
  ];

  const renderTable = (data) => (
    <TableContainer
      component={Paper}
      sx={{ marginBottom: "20px", backgroundColor: "transparent", maxWidth: "1000px", marginLeft: "auto", marginRight: "auto", display: "block"}}
    >
      <Table>
        <TableHead>
          <TableRow>
            <StyledTableCell>Sl.</StyledTableCell>
            <StyledTableCell>Text</StyledTableCell>
            <StyledTableCell>No. of classes</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <StyledTableRow key={index}>
              <StyledTableCell>{row.sl}</StyledTableCell>
              <StyledTableCell>{row.text}</StyledTableCell>
              <StyledTableCell>{row.noOfClasses}</StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box
      className="container-sm"
      sx={{
        mt: 3,
      }}
    >
      <Typography variant="h6" gutterBottom style={{ color: "#495057" }}>
        Suggested course of study
      </Typography>
      <Typography variant="p" paragraph>
        Swamiji’s talks over the last four decades have culminated in a
        systematic collection of recordings of classes on the Upanishads,
        Bhagavad Gita, Brahma Sutra and other Vedanta texts. This extensive
        collection of close to 6,000 hours of systematic teachings is one of a
        kind and only possible from a yogin as great as Swamiji. Any student who
        studies and learns from these recordings can receive as much benefit as
        one who studies a very high-quality academic course in Vedanta, as well
        as the blessings of being a seeker of the spiritual wisdom of our Rishis
        and Masters.
        <br />
        <br />
        While it would be most ideal if one could listen to all the recordings
        to enjoy the vision and wisdom of Vedanta, Swamiji has kindly offered a
        suggested course of study for those who would like some guidance on how
        to make a start. Swamiji has suggested a particular group of texts as a
        three-step course of study. The programme starts with a few recommended
        basic texts and gradually builds into the slightly more complex ones.
        <br />
        <br />
        &emsp;Here is the suggested course of study:
      </Typography>
      <Typography
        variant="p"
        sx={{ fontWeight: 700 }}
        style={{ color: "#495057" }}
      >
        Level 1 :
        <br />
        <br />
      </Typography>
      {renderTable(dataLevel1)}

      <Typography
        variant="p"
        sx={{ fontWeight: 700 }}
        style={{ color: "#495057" }}
      >
        Level 2 :
        <br />
        <br />
      </Typography>
      {renderTable(dataLevel2)}

      <Typography
        variant="p"
        sx={{ fontWeight: 700 }}
        style={{ color: "#495057" }}
      >
        Level 3 :
        <br />
        <br />
      </Typography>
      {renderTable(dataLevel3)}
      <Typography variant="p" sx={{ mt: 3 }}>
        All other texts are optional and they can be studied in any order
        <br />
        <br />
        In case one would like more guidance from Swamiji one may write to
        mantraglobus@gmail.com
        <br />
        <br />
        Enjoy the course, discover and claim your spiritual Self.
        <br />
        <br />
      </Typography>
      <Typography variant="h5" gutterBottom style={{ color: "#495057" }}>
        Frequently Asked Questions
      </Typography>

      <Typography variant="h6" gutterBottom style={{ color: "#495057" }}>
        1. Do I need to have enough disk space in my mobile for downloading and
        installing the app?
      </Typography>
      <Typography variant="p" gutterBottom>
        Yes. If you face any difficulty due to disk space constraints, please
        create enough space in your mobile device.
        <br />
        <br />
      </Typography>

      <Typography variant="h6" gutterBottom style={{ color: "#495057" }}>
        2. Is email necessary for login?
      </Typography>
      <Typography variant="p" gutterBottom>
        Yes, your email ID is mandatory for login.
        <br />
        <br />
      </Typography>

      <Typography variant="h6" gutterBottom style={{ color: "#495057" }}>
        3. What are the login methods?
      </Typography>
      <Typography variant="p" gutterBottom>
        You can login using any of the following email accounts:
      </Typography>
      <ul>
        <li>
          <Typography variant="p" sx={{ fontSize: "20px" }}>
            a. Facebook :
          </Typography>
          <Typography variant="p" gutterBottom>
            <ul>
              If you have a Facebook account, you could use the same account to
              log in. Click on the Facebook button so that you will get logged
              in via your Facebook account.
            </ul>
          </Typography>
        </li>
        <li>
          <Typography variant="p" sx={{ fontSize: "20px" }}>
            b. Google:
          </Typography>
          <Typography variant="p" gutterBottom>
            <ul>
              If you want to log in via your Google account, click on the Google
              button and select the appropriate Google account (already
              configured in the mobile) and you will be logged in automatically.
            </ul>
          </Typography>
        </li>
        <li>
          <Typography variant="p" sx={{ fontSize: "20px" }}>
            c. New Registration (apart from above two options)
          </Typography>
          <Typography variant="p" gutterBottom>
            <ul>
              {" "}
              Click on the ‘Sign Up’ button to open up the Registration screen.
              Enter your name, email ID, mobile number and password and click on
              ‘Create Account’. An OTP (one-time password) will then be sent to
              the email provided. Please enter the same in the appropriate text
              boxes to authenticate your login. Please use this email and
              password for Sign-In.
            </ul>
          </Typography>
        </li>
      </ul>

      <Typography variant="h6" gutterBottom style={{ color: "#495057" }}>
        4. How can I reset the password if it’s forgotten?
      </Typography>
      <Typography variant="p" gutterBottom>
        Click on ‘Forgot Password’ in the Home page and enter your email ID for
        the respective login account. An OTP will be sent to your email ID.
        Enter it in the text boxes that enable you to enter the new password.
        <br />
        <br />
      </Typography>

      <Typography variant="h6" gutterBottom style={{ color: "#495057" }}>
        5. How can I purchase a CD?
      </Typography>
      <Typography variant="p" gutterBottom>
        CDs can be purchased online, navigating through the following path:
        Discourses -{">"} Category (eg: Vedanta Classes) -{">"} Sub category
        (eg: Gita) -{">"} Topics (eg: Astavakra-Gita) -{">"} CDs. Clicking on
        the price icon takes you to the payment page.
        <br />
        <br />
      </Typography>

      <Typography variant="h6" gutterBottom style={{ color: "#495057" }}>
        6. When can I start downloading / listening to the lectures and hear
        them offline?
      </Typography>
      <Typography variant="p" gutterBottom>
        The Lectures will be available for download in the section ‘My Library’
        -{">"} ‘Lectures’ (only after you purchase the respective CD) for
        hearing them offline. But the lectures available in the ‘Lectures’
        section post purchase can be streamed online at any time.
        <br />
        <br />
      </Typography>

      <Typography variant="h6" gutterBottom style={{ color: "#495057" }}>
        7. Whom should I contact for assistance when required?
      </Typography>
      <Typography variant="p" gutterBottom>
        Please write to sastraprakasika@gmail.com with any queries and for
        assistance.
        <br />
        <br />
      </Typography>

      <Typography variant="h6" gutterBottom style={{ color: "#495057" }}>
        8. What is the Playlist for?
      </Typography>
      <Typography variant="p" gutterBottom>
        The Playlist facilitates the grouping and sequencing of the lectures
        under one ‘roof’ and enables you to play them as desired.
        <br />
        <br />
      </Typography>

      <Typography variant="h6" gutterBottom style={{ color: "#495057" }}>
        9. Will I receive a receipt from a mail post purchase?
      </Typography>
      <Typography variant="p" gutterBottom>
        Yes, you will receive a Receipt of Purchase via mail from the Play Store
        / App Store after you complete the purchase of a CD.
        <br />
        <br />
      </Typography>

      <Typography variant="h6" gutterBottom style={{ color: "#495057" }}>
        10. Where can I check for the list of CDs pertaining to a common subject
        (e.g., Gita, Vedanta, etc…)?
      </Typography>
      <Typography variant="p" gutterBottom>
        Please visit the ‘Search’ section and type the respective keyword in the
        text box provided.
      </Typography>
      <Typography variant="p" gutterBottom>
        On typing the keywords, all the possible matching results that are
        available in the System are listed. Clicking on any of the results will
        navigate you to the respective files.
        <br />
        <br />
      </Typography>
      <Typography variant="p" gutterBottom>
        You will be provided, near the Search box, with a combined set of
        results from both ‘Discourses’ and ‘My Library’.
        <br />
      </Typography>
      <Typography variant="p" gutterBottom>
        By default, the search will be among all the available Discourses and
        list the results accordingly.
        <br />
        <br />
      </Typography>
      <Typography variant="p" gutterBottom>
        On selecting only the ‘My Library’ option, search results will be from
        within the list of lectures you have already purchased.
        <br />
        <br />
      </Typography>

      <Typography variant="h6" gutterBottom style={{ color: "#495057" }}>
        11. How may I learn of the release of new CDs, if any?
      </Typography>
      <Typography variant="p" gutterBottom>
        When new CDs are released, registered users will receive a notification
        (in their mobile in which the mobile application is installed) regarding
        the release of new CDs.
        <br />
        <br />
      </Typography>

      <Typography variant="h6" gutterBottom style={{ color: "#495057" }}>
        12. May any credit cards/debit cards be used for purchases in iOS?
      </Typography>
      <Typography variant="p" gutterBottom>
        The credit/debit cards that have been mapped to the Apple Account
        (iPhone) may be used for payments.
        <br />
        <br />
      </Typography>
      <Typography variant="p" gutterBottom>
        &emsp; – Any credit/debit card can be mapped through Settings -{">"}{" "}
        Select the Apple ID -{">"} iTunes & App Store -{">"} Select Apple ID -
        {">"} View Apple ID -{">"} Manage Payments -{">"} Add Payment Method.
        <br />
        <br />
      </Typography>
      <Typography variant="p" gutterBottom>
        &emsp; – Any number of credit and debit cards can be mapped to the apple
        account.
        <br />
        <br />
      </Typography>
      <Typography variant="p" gutterBottom>
        &emsp; – The cards that are appearing at the top of the payment method
        list will be used for payments by default.
        <br />
        <br />
      </Typography>
      <Typography variant="p" gutterBottom>
        &emsp; – In order to change the default payment method, go to Settings -
        {">"} Select the Apple ID -{">"} iTunes & App Store -{">"} Select the
        Apple ID -{">"}
        View Apple ID -{">"} Manage Payments.
        <br />
        <br />
        <br />
        <br />
      </Typography>
    </Box>
  );
}

export default HelpSupport;